import { Attachment, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  List,
  Switch,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import {
  CharacterMetadata,
  ContentBlock,
  EntityInstance,
  RawDraftContentState,
  RawDraftEntity,
} from 'draft-js';
import { editorStateFromRaw } from 'megadraft';
import { Dispatch, SyntheticEvent, useState } from 'react';
import { PossibleActions } from '../../../../components/Draft/DraftActions';
import {
  patternPeopleLinkRegex,
  patternRecipeLinkRegex,
  patternSlideShowRegex,
} from './constants';
import { EntityType, GetLinksType, LabelLinkType, LinkType } from './types';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useLazyQuery } from '@apollo/client';
import {
  GetBodyWithSuggestedLinksQuery,
  GetBodyWithSuggestedLinksQueryVariables,
  BrandKey,
  SuggestInternalLinksQuery,
  SuggestInternalLinksQueryVariables,
} from '../../../../__generated__/queries-web';
import {
  NotificationTypeEnum,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import {
  GET_BODY_WITH_SUGGESTED_LINKS,
  SUGGEST_INTERNAL_LINKS,
} from '../../../../apollo/queries/suggestLinks.web.graphql';
import { actions, ArticleEditAction } from '../../reducer';

const getEntities = (editorState: typeof editorStateFromRaw) => {
  const content = editorState.getCurrentContent();
  const entities: EntityType[] = [];
  content.getBlocksAsArray().forEach((block: ContentBlock) => {
    let selectedEntity: Omit<EntityType, 'start' | 'end'>;
    const filterEntityRanges = (character: CharacterMetadata) => {
      const entity = character.getEntity();
      if (entity !== null) {
        const entityContent: EntityInstance = content.getEntity(entity);
        selectedEntity = {
          entityKey: entity,
          blockKey: block.getKey(),
          entity: entityContent,
        };
        return true;
      }
      return false;
    };
    const callback = (start: number, end: number) =>
      // eslint-disable-next-line fp/no-mutating-methods
      entities.push({ ...selectedEntity, start, end });

    block.findEntityRanges(filterEntityRanges, callback);
  });
  return entities;
};

const getLinks = (
  textLinks: string[],
  entity?: RawDraftContentState['entityMap'],
): GetLinksType[] => {
  const getType = (
    type: LinkType,
    duplicate: boolean,
    link?: string,
  ): LabelLinkType => {
    if (duplicate) return 'Doublon';

    if (
      link &&
      [PossibleActions.LINK, PossibleActions.INTERNAL_LINK].includes(type)
    ) {
      if (link.match(new RegExp(patternRecipeLinkRegex))) return 'Recipe';
      if (link.match(new RegExp(patternSlideShowRegex))) return 'Diapo';
    }

    if (
      type === PossibleActions.LINK &&
      link?.match(new RegExp(patternPeopleLinkRegex))
    )
      return 'People';

    return 'Lien';
  };

  const entriesReduce = (
    accum: GetLinksType[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [_, { data, type }]: [unknown, RawDraftEntity],
    i: number,
  ) => {
    const currentLink = data?.url;
    return [
      ...accum,
      {
        label: `${data?.children || textLinks[i]}`,
        link: `${currentLink}`,
        type: getType(
          type as LinkType,
          !!accum.find(({ link }) => link === currentLink),
          currentLink,
        ),
      },
    ];
  };

  return Object.entries(entity || {})?.reduce(entriesReduce, []);
};

const SuggestLinksDialog = ({
  onClose,
  body,
  dispatch,
  suggestedLinks,
}: {
  onClose: () => void;
  body?: string;
  dispatch: Dispatch<ArticleEditAction>;
  suggestedLinks: SuggestInternalLinksQuery['suggestInternalLinks'];
}) => {
  const { pushNotification } = useNotification();
  const [selectedLinks, setSelectedLinks] = useState<string[]>([]);

  // Helper function to highlight the word in the sentence
  const highlightWordInSentence = (sentence: string, word: string) => {
    const regex = new RegExp(`(${word})`, 'gi');
    const parts = sentence.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === word.toLowerCase()) {
        return <strong key={index}>{part}</strong>;
      }
      return part;
    });
  };

  const handleLinkToggle = (linkUrl: string) => {
    setSelectedLinks((prev) => {
      if (prev.includes(linkUrl)) {
        return prev.filter((url) => url !== linkUrl);
      }
      return [...prev, linkUrl];
    });
  };

  const [
    addSuggestedLinks,
    { loading: loadingAddSuggestedLinks },
  ] = useLazyQuery<
    GetBodyWithSuggestedLinksQuery,
    GetBodyWithSuggestedLinksQueryVariables
  >(GET_BODY_WITH_SUGGESTED_LINKS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: "Une erreur est survenue lors de l'ajout des liens",
        type: NotificationTypeEnum.error,
      });
    },
  });

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Suggestion de liens internes
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          {suggestedLinks.length > 0 ? (
            suggestedLinks.map((link, index) => {
              return (
                <Box key={link.url} sx={{ mx: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ fontWeight: 'bold' }}>{link.word}</Box>

                    <Box sx={{ fontStyle: 'italic' }}>
                      {highlightWordInSentence(link.sentence, link.word)}
                    </Box>
                    <Link
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      underline="hover"
                      sx={{ color: 'black', textDecoration: 'underline' }}
                    >
                      {link.title}
                    </Link>
                    <Box>{link.relevance}</Box>
                    <FormControlLabel
                      sx={{
                        justifyContent: 'center',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px',
                        },
                      }}
                      control={
                        <Switch
                          checked={selectedLinks.includes(link.url)}
                          onChange={() => handleLinkToggle(link.url)}
                          size="small"
                        />
                      }
                      label="Ajouter"
                    />
                  </Box>
                  {index < suggestedLinks.length - 1 && (
                    <Divider sx={{ mt: 2, mb: 2 }} />
                  )}
                </Box>
              );
            })
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography>Aucun lien trouvé</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {loadingAddSuggestedLinks ? (
          <CircularProgress size={16} />
        ) : (
          <Button
            variant="outlined"
            onClick={() => {
              if (body && selectedLinks.length > 0) {
                // Filter suggestedLinks to only include selected ones
                const selectedSuggestedLinks = suggestedLinks.filter((link) =>
                  selectedLinks.includes(link.url),
                );

                addSuggestedLinks({
                  variables: {
                    body,
                    links: selectedSuggestedLinks.map((link) => ({
                      url: link.url,
                      word: link.word,
                      sentence: link.sentence,
                      blockKey: link.blockKey,
                    })),
                  },
                  onCompleted: (data) => {
                    dispatch(
                      actions.updateField({
                        field: 'body',
                        value: JSON.parse(
                          data.getBodyWithSuggestedLinks.updatedBody,
                        ),
                      }),
                    );
                    onClose();
                  },
                });
              }
            }}
            disabled={selectedLinks.length === 0}
          >
            Ajouter ({selectedLinks.length})
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </>
  );
};

export const LinksContent = ({
  body,
  title,
  brandKey,
  dispatch,
  idArticle,
}: {
  body: RawDraftContentState | null;
  title: string;
  brandKey: BrandKey;
  dispatch: Dispatch<ArticleEditAction>;
  idArticle: string;
}) => {
  const [expanded, setExpanded] = useState<string>();
  const entities = getEntities(editorStateFromRaw(body));
  const textLinks: string[] = entities.reduce((accum: string[], entity) => {
    const findBlock = body?.blocks.find(({ key }) => key === entity.blockKey);
    if (findBlock) {
      return [...accum, findBlock.text.substring(entity.start, entity.end)];
    }
    return accum;
  }, []);

  const handleChange = (panel: string) => (
    event: SyntheticEvent,
    newExpanded: boolean,
  ) => setExpanded(newExpanded ? panel : '');
  const { pushNotification } = useNotification();

  const { openDialog, closeDialog } = useDialog();
  const links = getLinks(textLinks, body?.entityMap);

  const [
    suggestInternalLinks,
    { loading: loadingSuggestInternalLinks },
  ] = useLazyQuery<
    SuggestInternalLinksQuery,
    SuggestInternalLinksQueryVariables
  >(SUGGEST_INTERNAL_LINKS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: 'Une erreur est survenue lors de la complétion du diaporama',
        type: NotificationTypeEnum.error,
      });
    },
    onCompleted: (data) => {
      openDialog(
        <SuggestLinksDialog
          body={JSON.stringify(body)}
          onClose={() => closeDialog()}
          dispatch={dispatch}
          suggestedLinks={data.suggestInternalLinks}
        />,
      );
    },
  });

  return (
    <Accordion
      sx={{
        width: '100%',
        boxShadow: 'none',
        border: 'solid',
        borderWidth: '1px 0 1px 0',
        borderColor: 'divider',
        '&:not(:last-child)': {
          borderBottom: 0,
        },
        '&:before': {
          display: 'none',
        },
        marginTop: '0 !important',
      }}
      expanded={expanded === 'Liens'}
      onChange={handleChange('Liens')}
    >
      <AccordionSummary
        sx={{
          py: 2,
          '& .icon': {
            display: 'flex',
            '& p': {
              paddingLeft: '1rem',
              fontWeight: 500,
            },
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(180deg)',
          },
          minHeight: 'auto !important',
          '& .MuiAccordionSummary-content': {
            marginLeft: '1px',
            marginTop: '0 !important',
            marginBottom: '0 !important',
          },
        }}
        expandIcon={<ExpandMore />}
      >
        <div className="icon">
          <Attachment />
          <Typography>{`Liens de l'article (${links.length})`}</Typography>
        </div>
        <Box sx={{ marginLeft: '1rem', pt: '1.5px' }}>
          {loadingSuggestInternalLinks ? (
            <CircularProgress size={16} />
          ) : (
            <Box
              onClick={async (e) => {
                e.stopPropagation();
                await suggestInternalLinks({
                  variables: {
                    body: JSON.stringify(body),
                    title,
                    brandKey: brandKey as BrandKey,
                    existingLinksLabels: links.map(({ label }) => label),
                    idArticle,
                  },
                });
              }}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <AutoFixHighIcon
                sx={{
                  color: '#E91E63',
                  fontSize: '18px',
                }}
                aria-label="Bêta"
              />

              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: '#E91E63',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                Bêta
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '0.5px',
          borderTop: '1px solid rgba(0, 0, 0, .125)',
        }}
      >
        <List component="div" disablePadding>
          {links?.map(({ label, link, type }) => (
            <ul key={link}>
              <li>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    color: `${type === 'Doublon' ? 'green' : 'black'}`,
                  }}
                >
                  {type}
                </Typography>
                :{' '}
                <a
                  style={{
                    color: `${type === 'Doublon' ? 'green' : 'black'}`,
                    fontSize: '0.85rem',
                  }}
                  target="_blank"
                  rel="noreferrer"
                  href={link}
                >
                  {label}
                </a>
              </li>
            </ul>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
